import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
/* Hooks */
import useEventListener from 'src/hooks/useEventListener';
import { useSpring, animated } from 'react-spring';
/* Utils */
import { formatDollarAmount } from 'src/utils/formatter/currency';
import { getPricesAndDiscounts } from 'src/selectors/cart';
/* Constants */
import { ANIMATION_DURATION_DEFAULT } from 'src/containers/AddSkuPageV2/constants';
/* Components */
import OrderBreakdownLineItem from 'src/components/OrderSummary/Breakdown/OrderBreakdownLineItem';
import { Badge } from 'src/components/Elements/Badge';
import styles from './styles.scss';

const BREAKDOWN_CONTAINER_ID = 'AddSkuV2PriceBreakdown_BREAKDOWN_CONTAINER_ID';

const AddSkuV2PriceBreakdown = ({
  breakdown,
  topBorder,
  animationDuration = ANIMATION_DURATION_DEFAULT,
}) => {
  const [containerHeight, setContainerHeight] = useState(0);
  const [animatePrice, setAnimatePrice] = useState(false);

  const { adjustmentsByType } = breakdown;
  const hasAnswerAdjustments = Boolean(adjustmentsByType.answer.length);
  const basePriceStyles = cn({
    'paddingTop-small2': topBorder,
    'marginBottom-small': hasAnswerAdjustments,
  });

  /* Hooks */
  const animatedHeightStyles = useSpring({
    config: { duration: animationDuration },
    height: `${containerHeight}px`,
  });

  const count = useSpring({
    config: { duration: animationDuration },
    currentTotal: breakdown.amount || 0,
  });

  /* Data */
  const pricesAndDiscounts = getPricesAndDiscounts({ item: breakdown });

  /* Methods */

  const setHeight = () => {
    /*
      Because Design wants this to be a fixed position header and not sticky we need to insert a div that is the
      same height as our container in order to push the content down to the proper height
    */
    const container = document.getElementById(BREAKDOWN_CONTAINER_ID);
    setContainerHeight(container.offsetHeight);
  };

  const getSubtotal = (amount) => {
    if (amount === 0) return '$0';
    return formatDollarAmount({ amount });
  };

  /* Effects */
  useEffect(() => setHeight(), [adjustmentsByType.answer.length]);
  useEventListener({ eventName: 'resize', handler: setHeight });

  useEffect(() => {
    // Use a timeout to prevent the price animation from running on initial load of breakdown.amount.
    const timeoutId = setTimeout(() => {
      if (breakdown.amount >= 0) setAnimatePrice(true);
    }, 1000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [breakdown.amount]);

  return (
    <>
      {topBorder && <div className={cn(styles.borderElement)} />}
      <OrderBreakdownLineItem name="Base" className={basePriceStyles} {...pricesAndDiscounts} />
      <animated.div style={animatedHeightStyles}>
        <div id={BREAKDOWN_CONTAINER_ID}>
          {adjustmentsByType.answer.map((adj, i) => (
            <OrderBreakdownLineItem
              name={adj.name}
              amount={`+ ${adj.amount_formatted}`}
              key={`${i}name`}
              className={styles.lineItem}
              amountStyles={styles.amountStyles}
              secondary
            />
          ))}
        </div>
      </animated.div>

      <div className={styles.totalContainer}>
        <div className="flex justifyContentSpaceBetween p0 n900 text-weight-bold">
          <p className="p0 n900 text-weight-bold">Service Subtotal</p>
          <animated.div>
            {animatePrice
              ? count.currentTotal.interpolate((amount) => getSubtotal(amount))
              : getSubtotal(breakdown.amount)}
          </animated.div>
        </div>
        {breakdown.savings > 0 && (
          <div className="marginTop-tiny1 text-align-right">
            <Badge variant="outlined" color="blue" size="small">
              {`Member Savings ${formatDollarAmount({
                amount: breakdown.savings,
                truncateEvenDollars: true,
              })}`}
            </Badge>
          </div>
        )}
      </div>
    </>
  );
};

AddSkuV2PriceBreakdown.propTypes = {
  className: PropTypes.string,
  topBorder: PropTypes.bool,
  animationDuration: PropTypes.number,
  breakdown: PropTypes.shape({
    amount: PropTypes.number,
    savings: PropTypes.number,
    adjustmentsByType: PropTypes.shape({
      answer: PropTypes.array,
    }),
  }),
};
export default AddSkuV2PriceBreakdown;
